import React, { useEffect, useState } from 'react';
import { getLoadProfiles } from '../../services/SettingsService';

const RISK_LABELS = { CONSERVATIVE: '🛡️ Conservador', MODERATE: '⚖️ Moderado', AGGRESSIVE: '🚀 Agressivo' };
const RISK_ORDER = ['CONSERVATIVE', 'MODERATE', 'AGGRESSIVE'];

// Campos do perfil (ordem de exibição no resumo) → rótulo curto + formatação pra leigo.
const FIELD_LABELS = {
    maxExposurePercent:      v => `Exposição máx. ${v}%`,
    maxOpenPositions:        v => `Máx. ${v} posições`,
    btcRegimeFilter:         v => `Filtro BTC ${v ? 'ligado' : 'desligado'}`,
    pumpDumpFilterPercent:   v => (v ? `Pump/dump ±${v}%` : 'Pump/dump desligado'),
    dailyDrawdownLimit:      v => `Perda diária ${v}%`,
    weeklyDrawdownLimit:     v => `Perda semanal ${v}%`,
    stopLossCooldownMinutes: v => `Cooldown ${v}min`,
    regimeRangeReclaim:      v => `Laterais largas ${v ? 'ligado' : 'desligado'}`,
};
const RISK_FIELDS = Object.keys(FIELD_LABELS);

// Nível de "ousadia" 1–3 só pra desenhar a barrinha.
const RISK_BAR = { CONSERVATIVE: 1, MODERATE: 2, AGGRESSIVE: 3 };

function norm(v) { return (v === null || v === undefined || v === '') ? null : Number(v); }

// Detecta qual preset bate com os valores atuais (ou null = personalizado). Espelha
// detectRiskProfile do backend (pump&dump: null e 0 = ambos desligado).
function detectProfile(profiles, s) {
    for (const name of RISK_ORDER) {
        const p = profiles[name];
        if (!p) continue;
        const ok =
            norm(s.maxExposurePercent) === p.maxExposurePercent &&
            norm(s.maxOpenPositions) === p.maxOpenPositions &&
            (s.btcRegimeFilter === true) === p.btcRegimeFilter &&
            (norm(s.pumpDumpFilterPercent) || 0) === (norm(p.pumpDumpFilterPercent) || 0) &&
            norm(s.dailyDrawdownLimit) === p.dailyDrawdownLimit &&
            norm(s.weeklyDrawdownLimit) === p.weeklyDrawdownLimit &&
            norm(s.stopLossCooldownMinutes) === p.stopLossCooldownMinutes &&
            (s.regimeRangeReclaim === true) === p.regimeRangeReclaim;
        if (ok) return name;
    }
    return null;
}

/**
 * Seletor de Perfil de Risco — 3º eixo de carga. Um botão pré-preenche os 7 knobs de risco;
 * o usuário ainda pode ajustar fino cada campo abaixo (vira "Personalizado").
 *
 * props:
 *  - settings  (objeto de settings atual, pra detectar o perfil ativo)
 *  - onChange(field, value)  (mesmo da LoadProfiles — aplica campo a campo)
 */
function RiskProfile({ settings, onChange }) {
    const [meta, setMeta] = useState(null);

    useEffect(() => {
        getLoadProfiles().then(setMeta).catch(() => { /* silencioso */ });
    }, []);

    if (!meta || !meta.risk) return null;

    const profiles = meta.risk.profiles || {};
    const recommended = meta.risk.recommended;
    const current = detectProfile(profiles, settings || {});

    function applyProfile(name) {
        const p = profiles[name];
        if (!p) return;
        RISK_FIELDS.forEach(f => onChange(f, p[f] === undefined ? null : p[f]));
        onChange('riskProfile', name);
    }

    const sel = current ? profiles[current] : null;

    return (
        <div className='card border-0 shadow-sm mb-3'>
            <div className='card-body'>
                <h6 className='fw-bold mb-1'>🎚️ Perfil de Risco</h6>
                <small className='text-muted d-block mb-2'>
                    Escolha um nível e preenchemos os controles de risco com valores coerentes. Você ainda pode ajustar cada campo abaixo.
                </small>

                <div className='btn-group w-100 mb-2' role='group'>
                    {RISK_ORDER.filter(k => profiles[k]).map(k => (
                        <button key={k} type='button'
                            className={`btn btn-sm ${current === k ? 'btn-primary' : 'btn-outline-primary'} position-relative`}
                            onClick={() => applyProfile(k)}>
                            {RISK_LABELS[k]}
                            {recommended === k && (
                                <span className='badge bg-success ms-1' style={{ fontSize: '0.6rem' }}>🎯</span>
                            )}
                        </button>
                    ))}
                </div>

                <div className='d-flex gap-1 align-items-center mb-2' style={{ height: 8 }}>
                    {[1, 2, 3].map(i => (
                        <span key={i} style={{ height: 6, flex: 1, borderRadius: 2,
                            background: i <= (RISK_BAR[current] || 0) ? '#fd7e14' : '#e9ecef' }} />
                    ))}
                </div>

                {current === null ? (
                    <small className='text-muted d-block'>
                        ⚙️ <strong>Personalizado</strong> — seus controles não batem com nenhum preset. Clique num nível acima para redefinir.
                    </small>
                ) : (
                    <small className='text-muted d-block'>
                        {RISK_FIELDS.map(f => FIELD_LABELS[f](sel[f])).join(' · ')}.
                    </small>
                )}
                {recommended && current !== recommended && (
                    <small className='text-muted d-block mt-1'>
                        🎯 Recomendado para começar: <strong>{RISK_LABELS[recommended]}</strong>.
                    </small>
                )}
            </div>
        </div>
    );
}

export default RiskProfile;
